import { AlertCircle, CheckCircle2, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import AppHeader from "@/components/AppHeader";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");
  const intervalRef = useRef(null);

  const checkPayment = useCallback(async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`,
      { credentials: "include" },
    );
    if (!response.ok) throw new Error("Payment status could not be verified");
    return response.json();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }
    let isActive = true;
    const pollPayment = async () => {
      try {
        const payment = await checkPayment();
        if (payment.payment_status === "paid" && isActive) {
          setStatus("paid");
          return true;
        }
        return false;
      } catch (error) {
        if (isActive) {
          setStatus("error");
        }
        return true;
      }
    };
    const startPolling = async () => {
      const reachedTerminalState = await pollPayment();
      if (!reachedTerminalState && isActive) {
        intervalRef.current = window.setInterval(pollPayment, 2500);
      }
    };
    startPolling();
    return () => {
      isActive = false;
      window.clearInterval(intervalRef.current);
    };
  }, [checkPayment, sessionId]);

  return (
    <div className="judge-app" data-testid="payment-success-page">
      <AppHeader onNewRound={() => navigate("/")} />
      <main className="payment-result">
        {status === "paid" ? (
          <CheckCircle2 size={42} />
        ) : status === "error" ? (
          <AlertCircle size={42} />
        ) : (
          <LoaderCircle className="payment-spinner" size={42} />
        )}
        <p className="eyebrow">PFJudgePro Pro</p>
        <h1>
          {status === "paid"
            ? "Pro is ready."
            : status === "error"
              ? "We couldn’t confirm this checkout."
              : "Confirming your access."}
        </h1>
        <p>
          {status === "paid"
            ? "Your Pro tools are now active on your account."
            : status === "error"
              ? "No access was changed. Return to Pro to try checkout again."
              : "Please keep this page open while your secure purchase is confirmed."}
        </p>
        {status === "paid" && (
          <Link
            className="primary-action"
            to="/"
            data-testid="return-to-pro-workspace-link"
          >
            Return to workspace
          </Link>
        )}
        {status === "error" && (
          <Link
            className="secondary-action"
            to="/pro"
            data-testid="return-to-pro-page-link"
          >
            Return to Pro
          </Link>
        )}
      </main>
    </div>
  );
}
